import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ClientLayout from '@/components/feature/ClientLayout';
import ProjectIntentSection from '@/components/feature/ProjectIntentSection';
import ProjectStoryTimeline from '@/components/feature/ProjectStoryTimeline';
import FilesTab from '@/pages/management/projects/components/FilesTab';
import ScheduleTab from '@/pages/management/projects/components/ScheduleTab';
import ChecklistTab from '@/pages/management/projects/components/ChecklistTab';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import ClientNotificationsEmbed from '@/pages/client/portal/components/ClientNotificationsEmbed';
import ClientDailyLogEmbed from '@/pages/client/portal/components/ClientDailyLogEmbed';
import ClientChangeOrdersEmbed from '@/pages/client/portal/components/ClientChangeOrdersEmbed';
import { useClientProject } from '@/hooks/useClientProject';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import { useProjectStages } from '@/hooks/useProjectStages';
import {
  SkeletonHero,
  SkeletonStatRow,
  SkeletonCard,
  SkeletonStageTimeline,
  SkeletonText,
  SkeletonProgressBar,
  SkeletonBase,
} from '@/components/base/Skeleton';

const stageStatusStyle: Record<string, { bg: string; text: string; border: string }> = {
  completed:   { bg: '#F0FDF4', text: '#059669', border: '#BBF7D0' },
  active:      { bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74' },
  in_progress: { bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74' },
  upcoming:    { bg: '#F9FAFB', text: '#D1D5DB', border: '#E5E7EB' },
  not_started: { bg: '#F9FAFB', text: '#D1D5DB', border: '#E5E7EB' },
  delayed:     { bg: '#FEF2F2', text: '#EF4444', border: '#FECACA' },
};

function fmtDate(d: string | null): string {
  if (!d) return 'TBC';
  return new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
}

type ClientTab = 'My Project' | 'Daily Log' | 'Schedule' | 'Checklist' | 'Files' | 'Change Orders' | 'Notifications';
const CLIENT_TAB_KEYS: ClientTab[] = ['My Project', 'Daily Log', 'Schedule', 'Checklist', 'Files', 'Change Orders', 'Notifications'];

export default function ClientPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const {
    projects,
    selectedProject,
    projectId,
    projectCode,
    loading: projectLoading,
    selectProject,
  } = useClientProject();

  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ClientTab>('My Project');
  const clientTabs: ClientTab[] = CLIENT_TAB_KEYS;
  const tabLabels: Record<ClientTab, string> = {
    'My Project': t('client.tab.myProject'),
    'Daily Log': t('client.tab.dailyLog'),
    'Schedule': t('client.tab.schedule'),
    'Checklist': t('client.tab.checklist'),
    'Files': t('client.tab.files'),
    'Change Orders': t('client.tab.changeOrders'),
    'Notifications': t('client.tab.notifications'),
  };

  // Subtle background refresh indicator
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedProjectId = params.get('projectId');
    const requestedTab = params.get('tab');

    if (requestedProjectId) {
      const match = projects.find((project) => project.id === requestedProjectId);
      if (match && match.id !== selectedProject?.id) {
        selectProject(match);
      }
    }

    if (requestedTab && CLIENT_TAB_KEYS.includes(requestedTab as ClientTab)) {
      setActiveTab(requestedTab as ClientTab);
    }
  }, [location.search, projects, selectedProject?.id, selectProject]);

  // ── UNIFIED: Single source of truth for project progress ──
  const summary = useProjectSummary(projectId);
  const summaryLoaded = summary.loaded;

  // ── REAL STAGES: derived from jphph_project_schedule_items ──
  const { stages: realStages, loading: stagesLoading } = useProjectStages(projectId);

  // ── First-load detection: use ref so we don't flash skeleton on every refresh ──
  const firstLoadDoneRef = useRef(false);
  const isFirstLoad = !firstLoadDoneRef.current && (projectLoading || summary.loading || stagesLoading);

  useEffect(() => {
    if (!isFirstLoad && (projectLoading || summary.loading || stagesLoading)) {
      // Data is refreshing in background (not first load)
      setIsRefreshing(true);
    } else {
      setIsRefreshing(false);
    }
    if (!projectLoading && !summary.loading && !stagesLoading) {
      firstLoadDoneRef.current = true;
    }
  }, [projectLoading, summary.loading, stagesLoading, isFirstLoad]);

  // ── Progress: only show real numbers, never default to 0 ──
  const overallProgress = summaryLoaded ? summary.projectProgress : undefined;

  // ── Stage counts: only computed after stages are loaded ──
  const totalStages = realStages.length > 0 ? realStages.length : 5;
  const completedStages = summaryLoaded
    ? (overallProgress === 100 ? totalStages : Math.max(0, realStages.filter((s) => s.status === 'completed').length))
    : undefined;

  const currentStageName = summaryLoaded && overallProgress === 100
    ? 'Completed'
    : realStages.find((s) => s.status === 'in_progress')?.stage_name
    || realStages[realStages.length - 1]?.stage_name
    || 'Planning';
  const currentStagePercent = summaryLoaded && overallProgress === 100
    ? 100
    : (realStages.find((s) => s.status === 'in_progress')?.completion_percent ?? 0);

  // ── No project linked (after loading completes) ──
  if (!projectLoading && (!selectedProject || projects.length === 0)) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh] px-6">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-2xl bg-[#F9FAFB] flex items-center justify-center mx-auto mb-4">
              <i className="ri-home-4-line text-3xl text-[#D1D5DB]"></i>
            </div>
            <h2 className="text-[#1A1A1A] font-bold text-lg mb-2">{t('client.portal.noProjectLinked')}</h2>
            <p className="text-[#9CA3AF] text-sm leading-relaxed">
              {t('client.portal.noProjectDesc')}
            </p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const p = selectedProject;

  // ── SKELETON LOADING STATE for first load ──
  if (isFirstLoad) {
    return (
      <ClientLayout>
        <div className="w-full overflow-x-hidden">
          <SkeletonHero />
          <div className="bg-[#1A1A1A] px-5 py-3">
            <SkeletonProgressBar />
          </div>
          {/* Tab bar */}
          <div className="bg-white border-b border-gray-100 sticky top-14 z-20">
            <div className="flex overflow-x-auto scrollbar-hide">
              {clientTabs.map((tab) => (
                <button
                  key={tab}
                  className={`flex-shrink-0 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap relative ${
                    activeTab === tab
                      ? 'border-[#E8621A] text-[#E8621A]'
                      : 'border-transparent text-[#9CA3AF]'
                  }`}
                >
                  {tabLabels[tab]}
                </button>
              ))}
            </div>
          </div>
          <div className="px-4 lg:px-6 pt-5 pb-28 lg:pb-10 max-w-full overflow-x-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 space-y-4">
                <SkeletonCard />
                <SkeletonCard />
                <div className="grid grid-cols-2 gap-3">
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <SkeletonBase width="50%" height={16} className="mb-4" />
                  <SkeletonStageTimeline count={5} />
                </div>
                <SkeletonCard />
              </div>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      {/* Subtle background refresh indicator */}
      {isRefreshing && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-white/90 backdrop-blur-sm border border-[#FDBA74]/40 rounded-full px-4 py-1.5 flex items-center gap-2 shadow-sm">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xs"></i>
          <span className="text-[#92400E] text-xs font-medium">Refreshing...</span>
        </div>
      )}

      <div className="w-full overflow-x-hidden">
        {/* Hero photo */}
        <div className="relative h-56 lg:h-64 overflow-hidden">
          <img
            src={
              p?.cover_image ||
              'https://readdy.ai/api/search-image?query=modern%20residential%20construction%20site%20New%20Zealand%20home%20building%20progress%20professional%20photography%20warm%20natural%20light%20clean%20background&width=800&height=500&seq=clienthero&orientation=landscape'
            }
            alt={p?.project_name ?? 'Your project'}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/90 via-[#1A1A1A]/30 to-transparent"></div>
          <div className="absolute bottom-5 left-5 right-5">
            <p className="text-white/50 text-[10px] uppercase tracking-widest mb-1">Your Project</p>
            <h1 className="text-white text-2xl font-bold leading-tight">{p?.project_name}</h1>
            <p className="text-white/60 text-sm mt-0.5">{p?.site_address ?? ''}</p>
          </div>
        </div>

        {/* Progress bar strip */}
        <div className="bg-[#1A1A1A] px-5 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm font-bold">
              {overallProgress !== undefined ? t('client.portal.complete', { percent: overallProgress }) : t('common.loading')}
            </span>
            <span className="text-white/50 text-xs">
              {completedStages !== undefined ? t('client.portal.stagesCount', { completed: completedStages, total: totalStages }) : ''}
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            {overallProgress !== undefined ? (
              <div
                className="h-full rounded-full bg-[#E8621A] transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              ></div>
            ) : null}
          </div>
          {currentStageName && overallProgress !== undefined ? (
            <p className="text-[#E8621A] text-xs font-semibold mt-1.5">
              {t('client.portal.currently', { stage: currentStageName, percent: currentStagePercent })}
            </p>
          ) : overallProgress === 100 ? (
            <p className="text-[#059669] text-xs font-semibold mt-1.5">
              <i className="ri-check-double-line mr-1"></i>{t('client.portal.projectCompleted')}
            </p>
          ) : null}
        </div>

        {/* Tab bar */}
        <div className="bg-white border-b border-gray-100 sticky top-14 z-20">
          <div className="flex overflow-x-auto scrollbar-hide">
            {clientTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-shrink-0 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap relative ${
                  activeTab === tab
                    ? 'border-[#E8621A] text-[#E8621A]'
                    : 'border-transparent text-[#9CA3AF]'
                }`}
              >
                {tabLabels[tab]}
              </button>
            ))}
          </div>
        </div>

        {/* Project selector (if multiple projects) */}
        {projects.length > 1 && (
          <div className="px-4 lg:px-6 pt-3 pb-0">
            <div className="flex items-center gap-2 overflow-x-auto">
              {projects.map((proj) => (
                <button
                  key={proj.id}
                  onClick={() => selectProject(proj)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    selectedProject?.id === proj.id
                      ? 'bg-[#E8621A] text-white'
                      : 'bg-[#F9FAFB] text-[#374151] border border-gray-100'
                  }`}
                >
                  {proj.project_name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 lg:px-6 pt-5 pb-28 lg:pb-10 max-w-full overflow-x-hidden">
          {/* ── My Project Tab ── */}
          {activeTab === 'My Project' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 space-y-4">
                {/* Latest from Japan Homes banner */}
                <div className="bg-[#FEF0E7] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg bg-[#E8621A] flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-2-line text-white text-xs"></i>
                    </div>
                    <span className="text-[#E8621A] text-xs font-bold">{t('client.portal.latestFromJH')}</span>
                  </div>
                  <p className="text-[#374151] text-sm leading-relaxed">
                    {t('client.portal.latestFromJHDesc')}
                  </p>
                </div>

                {/* Action required banner */}
                <div className="bg-white rounded-2xl border-2 border-[#E8621A]/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-alarm-warning-line text-[#E8621A] text-base"></i>
                    <span className="text-[#E8621A] text-sm font-bold">{t('client.portal.actionItems')}</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm leading-relaxed">
                    {t('client.portal.actionItemsDesc')}
                  </p>
                </div>

                {/* Quick actions */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => navigate('/client/instructions')}
                    className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-[#E8621A]/30 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#FEF0E7] flex items-center justify-center mb-3">
                      <i className="ri-file-list-3-line text-lg text-[#E8621A]"></i>
                    </div>
                    <div className="text-[#1A1A1A] text-sm font-semibold">{t('client.portal.instructions')}</div>
                    <div className="text-[#9CA3AF] text-xs mt-0.5">{t('client.portal.instructionsDesc')}</div>
                  </button>
                  <button
                    onClick={() => navigate('/client/questions')}
                    className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-[#E8621A]/30 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#F9FAFB] flex items-center justify-center mb-3">
                      <i className="ri-question-answer-line text-lg text-[#374151]"></i>
                    </div>
                    <div className="text-[#1A1A1A] text-sm font-semibold">{t('client.portal.askQuestion')}</div>
                    <div className="text-[#9CA3AF] text-xs mt-0.5">{t('client.portal.askQuestionDesc')}</div>
                  </button>
                </div>

                {/* Project Files shortcut card */}
                <button
                  onClick={() => navigate('/client/portal?tab=Files')}
                  className="w-full bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-[#E8621A]/30 transition-colors flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                    <i className="ri-folder-image-line text-xl text-[#E8621A]"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#1A1A1A] text-sm font-semibold">{t('client.portal.projectFiles')}</div>
                    <div className="text-[#9CA3AF] text-xs mt-0.5">{t('client.portal.projectFilesDesc')}</div>
                  </div>
                  <div className="text-[#E8621A] text-xs font-semibold whitespace-nowrap flex items-center gap-1">
                    {t('client.portal.viewFiles')} <i className="ri-arrow-right-s-line"></i>
                  </div>
                </button>

                {user && projectId && (
                  <ProjectIntentSection projectId={projectId} userId={user.id} projectType={p?.project_type} mode="edit" labelStyle="client" />
                )}
                {user && projectId && (
                  <ProjectStoryTimeline projectId={projectId} userId={user.id} mode="client" labelStyle="client" />
                )}
              </div>

              {/* Right sidebar — Build Journey with REAL stages */}
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="font-bold text-[#1A1A1A] text-sm mb-4">{t('client.portal.buildJourney')}</h3>
                  {stagesLoading && !firstLoadDoneRef.current ? (
                    <SkeletonStageTimeline count={5} />
                  ) : realStages.length > 0 ? (
                    <div className="space-y-0">
                      {realStages.map((stage, i) => {
                        const isFullyComplete = overallProgress === 100;
                        const stageStatus = isFullyComplete ? 'completed' : stage.status;
                        const style = stageStatusStyle[stageStatus] || stageStatusStyle.upcoming;
                        const isLast = i === realStages.length - 1;
                        const progress = isFullyComplete ? 100 : stage.completion_percent;
                        return (
                          <div key={stage.id} className="flex items-stretch gap-3">
                            <div className="flex flex-col items-center flex-shrink-0 w-8">
                              <div
                                className="w-7 h-7 rounded-full flex items-center justify-center border-2 text-[10px] font-bold flex-shrink-0 z-10"
                                style={{
                                  backgroundColor: style.bg,
                                  borderColor: style.border,
                                  color: style.text,
                                }}
                              >
                                {stageStatus === 'completed' ? (
                                  <i className="ri-check-line text-xs"></i>
                                ) : (
                                  i + 1
                                )}
                              </div>
                              {!isLast && (
                                <div
                                  className="w-0.5 flex-1 min-h-[20px]"
                                  style={{
                                    backgroundColor:
                                      stageStatus === 'completed' ? '#BBF7D0' : '#E5E7EB',
                                  }}
                                ></div>
                              )}
                            </div>
                            <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-4'}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span
                                  className="text-sm font-semibold leading-tight"
                                  style={{
                                    color: stageStatus === 'not_started' ? '#9CA3AF' : '#1A1A1A',
                                  }}
                                >
                                  {stage.stage_name}
                                </span>
                                {stageStatus === 'in_progress' && (
                                  <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                                    {t('client.portal.inProgress')}
                                  </span>
                                )}
                                {stageStatus === 'completed' && (
                                  <span className="text-[10px] font-bold text-[#059669] bg-[#F0FDF4] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                                    {t('client.portal.done')}
                                  </span>
                                )}
                              </div>
                              <div className="text-[#9CA3AF] text-xs mt-0.5">
                                {stage.start_date ? fmtDate(stage.start_date) : 'TBC'}
                                {' — '}
                                {stage.end_date ? fmtDate(stage.end_date) : 'TBC'}
                              </div>
                              {stage.item_count > 0 && (
                                <div className="text-[#9CA3AF] text-[10px] mt-0.5">
                                  {t('client.portal.itemsCompleted', { done: stage.completed_items, total: stage.item_count })}
                                </div>
                              )}
                              {(stageStatus === 'in_progress' || stageStatus === 'active') && progress > 0 && (
                                <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-[#E8621A]"
                                    style={{ width: `${progress}%` }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <i className="ri-loader-4-line text-2xl text-[#D1D5DB] mb-2"></i>
                      <p className="text-[#9CA3AF] text-sm">{t('client.portal.noStagesYet')}</p>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="font-bold text-[#1A1A1A] text-sm mb-3">{t('client.portal.projectDetails')}</h3>
                  <div className="space-y-2.5">
                    {[
                      { label: t('client.portal.managedBy'), value: 'Japan Homes' },
                      { label: t('client.portal.projectCode'), value: p?.project_code ?? '—' },
                      { label: t('client.portal.estCompletion'), value: p?.target_completion_date ? fmtDate(p.target_completion_date) : t('client.portal.datesTBC') },
                      { label: t('client.portal.status'), value: summaryLoaded ? (summary.projectStatus === 'complete' ? t('client.portal.completed') : summary.projectStatus.charAt(0).toUpperCase() + summary.projectStatus.slice(1)) : (p?.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1) : '—') },
                      { label: t('client.portal.type'), value: p?.project_type ?? '—' },
                      { label: t('client.portal.projectProgress'), value: overallProgress !== undefined ? `${overallProgress}%` : '—' },
                      { label: t('client.portal.checklistFinal'), value: summaryLoaded ? `${summary.checklistFinalProgress}%` : '—' },
                      { label: t('client.portal.scheduleFinal'), value: summaryLoaded ? `${summary.scheduleFinalProgress}%` : '—' },
                    ].map((row) => (
                      <div key={row.label} className="flex items-center justify-between gap-3">
                        <span className="text-[#9CA3AF] text-sm flex-shrink-0">{row.label}</span>
                        <span className={`text-[#374151] text-sm font-medium text-right truncate ${row.label === 'Status' && (overallProgress === 100 || row.value === 'Completed') ? 'text-[#059669]' : ''}`}>
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Daily Log Tab ── */}
          {activeTab === 'Daily Log' && (
            <div>
              <div className="bg-[#F0FDF4] rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-book-open-line text-[#059669] text-base"></i>
                  <span className="text-[#374151] text-sm font-bold">{t('client.tab.dailyLog')}</span>
                </div>
                <p className="text-[#374151] text-sm">
                  {t('client.tab.dailyLogDesc')}
                </p>
              </div>
              {projectId && <ClientDailyLogEmbed projectId={projectId} />}
            </div>
          )}

          {/* ── Files Tab (view-only) ── */}
          {activeTab === 'Files' && projectId && (
            <FilesTab projectId={projectId} canUpload={false} />
          )}

          {/* ── Schedule Tab (view-only) ── */}
          {activeTab === 'Schedule' && (
            <div>
              <div className="bg-[#F9FAFB] rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-calendar-schedule-line text-[#374151] text-base"></i>
                  <span className="text-[#374151] text-sm font-bold">{t('client.tab.projectProgramme')}</span>
                </div>
                <p className="text-[#9CA3AF] text-sm">
                  {t('client.tab.programmeDesc')}
                </p>
              </div>
              {projectId && <ScheduleTab projectId={projectId} canEdit={false} viewerRole="client" />}
            </div>
          )}

          {/* ── Checklist Tab (client review) ── */}
          {activeTab === 'Checklist' && (
            <div>
              <div className="bg-[#F9FAFB] rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-checkbox-multiple-line text-[#374151] text-base"></i>
                  <span className="text-[#374151] text-sm font-bold">{t('client.tab.projectChecklist')}</span>
                </div>
                <p className="text-[#9CA3AF] text-sm">
                  {t('client.tab.checklistDesc')}
                </p>
              </div>
              {projectId && <ChecklistTab projectId={projectId} viewerRole="client" />}
            </div>
          )}

          {/* ── Change Orders Tab ── */}
          {activeTab === 'Change Orders' && (
            <ClientChangeOrdersEmbed projectId={projectId} />
          )}

          {/* ── Notifications Tab (real data) ── */}
          {activeTab === 'Notifications' && (
            <ClientNotificationsEmbed userId={user?.id ?? null} profile={profile} projectId={projectId} />
          )}
        </div>
      </div>
    </ClientLayout>
  );
}
